<!-- src/components/dashboard/PendingActionsPanel.vue -->
<template>
  <!-- SECURITY GUARD: Strictly locked to authorised view -->
  <div v-if="isAuthorisedToView && allNotifications.length > 0" class="space-y-4 animate-fadeIn p-2 text-left">
    <div v-for="note in allNotifications" :key="note.id" class="relative group">
      
      <!-- Hover Glow Aura -->
      <div 
        class="absolute -inset-1 rounded-[32px] blur-2xl opacity-0 group-hover:opacity-30 transition duration-700" 
        :style="{ backgroundColor: note.color }"
      ></div>
      
      <div class="relative flex items-center bg-white border border-slate-100 rounded-[32px] shadow-sm transition-all hover:shadow-xl hover:border-[#20FCF1]/30 overflow-hidden">
        
        <!-- MAIN NAVIGATION SLOT -->
        <router-link 
          :to="note.to" 
          class="flex-grow flex items-center justify-between p-5 pr-2"
        >
          <div class="flex items-center gap-6">
            <div class="relative group-hover:scale-105 transition-transform duration-500">
              <div class="absolute inset-0 rounded-full blur-xl opacity-20" :style="{ backgroundColor: note.color }"></div>
              <div class="relative w-14 h-14 flex items-center justify-center">
                <!-- Shield Protocol SVG -->
                <svg class="absolute inset-0 w-full h-full" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" 
                    :fill="note.color" 
                    fill-opacity="0.1" 
                    :stroke="note.color" 
                    stroke-width="1.5"
                  />
                </svg>
                <component :is="note.icon" class="w-6 h-6" :style="{ color: note.color }" />
              </div>
            </div>
            
            <div class="min-w-0 text-left">
              <h4 class="text-[11px] font-black text-[#0A1630] uppercase tracking-tight leading-none truncate">
                {{ note.title }}
              </h4>
              <p class="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-2 leading-none">
                {{ note.description }}
              </p>
            </div>
          </div>
        </router-link>

        <div class="flex items-center gap-2 pr-6">
           <button 
            @click.stop.prevent="dismissNotification(note)"
            class="p-3 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-2xl transition-all group/trash"
           >
             <TrashIcon class="h-4 w-4 transition-transform group-hover/trash:scale-110" />
           </button>
           
           <router-link :to="note.to" class="bg-slate-50 text-slate-300 p-2.5 rounded-xl hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-inner">
              <ArrowRightIcon class="h-4 w-4" />
           </router-link>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="isAuthorisedToView && allNotifications.length === 0" class="py-20 text-center animate-fadeIn">
     <div class="bg-slate-50 w-20 h-20 rounded-[32px] flex items-center justify-center mx-auto mb-6 border border-slate-100 shadow-inner">
        <ShieldCheckIcon class="h-10 w-10 text-slate-200" />
     </div>
     <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest text-center">Registry Synchronised</h3>
     <p class="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-2 text-center">No outstanding actions for your department.</p>
  </div>
</template>

<script setup>
import { ref, onUnmounted, watch, computed } from 'vue';
import { db, auth } from '@/firebaseConfig';
import { collection, query, where, onSnapshot, doc, deleteDoc, updateDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';
import { 
  WrenchScrewdriverIcon, TruckIcon, 
  ArrowRightIcon, BanknotesIcon, ClipboardDocumentCheckIcon,
  ChatBubbleBottomCenterTextIcon, TrashIcon, ShieldCheckIcon
} from '@heroicons/vue/24/outline';

const props = defineProps({ targetUserId: { type: String, default: null } });
const userStore = useUserStore();
const emit = defineEmits(['update-count']);

const taskItems = ref([]);
const talkItems = ref([]);
const timesheetItems = ref([]);
const supportItems = ref([]);
const vehicleItems = ref([]);
const unsubs = [];

const isAuthorisedToView = computed(() => auth.currentUser?.uid === props.targetUserId);

const allNotifications = computed(() => {
  if (!isAuthorisedToView.value) return [];
  return [
    ...taskItems.value, ...talkItems.value, ...timesheetItems.value, 
    ...supportItems.value, ...vehicleItems.value
  ].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
});

const purgeRelatedTimesheetActions = async (submissionId) => {
  if (!auth.currentUser || !submissionId) return;
  try {
    // 1. Clean up local user notifications
    const notificationsRef = collection(db, 'userProfiles', auth.currentUser.uid, 'notifications');
    const snap = await getDocs(notificationsRef);
    const snapsToPurge = [];
    snap.docs.forEach(d => {
      const data = d.data();
      const matches = data.relatedId === submissionId || 
                      data.submissionId === submissionId || 
                      data.timesheetId === submissionId || 
                      (data.link && data.link.includes(submissionId));
      if (matches) {
        snapsToPurge.push(d.ref);
      }
    });
    for (const ref of snapsToPurge) {
      await deleteDoc(ref);
    }

    // 2. Clean up associated user_actions
    const actionsRef = collection(db, 'user_actions');
    const qActions = query(actionsRef, where('status', '==', 'Pending'));
    const actionsSnap = await getDocs(qActions);
    const actionsToUpdate = [];
    actionsSnap.docs.forEach(d => {
      const data = d.data();
      const matches = data.relatedId === submissionId || 
                      data.submissionId === submissionId || 
                      data.timesheetId === submissionId || 
                      (data.link && data.link.includes(submissionId)) ||
                      (data.targetUserId === auth.currentUser.uid && (data.link && data.link.includes(submissionId)));
      if (matches) {
        actionsToUpdate.push(d.ref);
      }
    });

    for (const ref of actionsToUpdate) {
      await updateDoc(ref, {
        status: 'Completed',
        isRead: true,
        completedAt: serverTimestamp()
      });
    }
  } catch (err) {
    console.warn("Manual dismissal secondary purge failed:", err);
  }
};

const dismissNotification = async (note) => {
  if (!confirm(`Authorise dismissal?`)) return;
  try {
    const colMap = { 'user_actions': 'user_actions', 'timesheet': 'timesheetSubmissions', 'support': 'supportRequestSubmissions' };
    const collectionName = colMap[note.type] || note.type;
    
    if (note.type === 'user_actions') {
      await deleteDoc(doc(db, collectionName, note.id));
    } else {
      await updateDoc(doc(db, collectionName, note.id), { status: 'Dismissed', 'formData.status': 'Dismissed' });
    }

    // Clean up associated actions if it's a timesheet
    if (note.type === 'timesheet') {
      await purgeRelatedTimesheetActions(note.id);
    }
  } catch (err) { console.error(err); }
};

const setupListeners = () => {
  unsubs.forEach(u => u()); unsubs.length = 0;
  if (!isAuthorisedToView.value) return;
  const profile = userStore.userProfile;
  const perms = userStore.permissions;
  const clientId = userStore.clientId;
  const userId = auth.currentUser?.uid;
  if (!clientId || !userId || !profile) return;

  // Resolve user's departments for strict filtering
  const myDepts = (profile.departments || []).map(d => d.toLowerCase().trim());
  if (profile.department) myDepts.push(profile.department.toLowerCase().trim());
  const uniqueMyDepts = [...new Set(myDepts)];

  // 1. PERSONAL SYSTEM ACTIONS
  unsubs.push(onSnapshot(query(collection(db, 'user_actions'), where('targetUserId', '==', userId), where('status', '==', 'Pending')), (snap) => {
    taskItems.value = snap.docs.map(d => ({ id: d.id, type: 'user_actions', title: d.data().title || 'Action Required', description: d.data().description, icon: ClipboardDocumentCheckIcon, color: '#EF4444', to: d.data().link || '/dashboard', timestamp: d.data().createdAt?.toMillis() || 0 }));
  }));

  // 2. TOOLBOX TALKS
  watch(() => userStore.pendingTalks, (talks) => {
    talkItems.value = (talks || []).map(t => ({ id: t.id, type: 'toolboxTalks', title: `Briefing: ${t.topic}`, description: 'Signature Required', icon: ChatBubbleBottomCenterTextIcon, color: '#F97316', to: { name: 'ToolboxTalkConfirmation', params: { talkId: t.id } }, timestamp: t.date?.toMillis() || 0 }));
  }, { immediate: true });

  // 3. TIMESHEET AUTHORISATIONS (STRICT DEPARTMENTAL SILO)
  if (profile.isDepartmentManager || profile.isDepartmentDirector || userStore.isAdmin) {
    unsubs.push(onSnapshot(query(collection(db, 'timesheetSubmissions'), where('clientId', '==', clientId), where('formData.status', '==', 'Pending Manager Approval')), (snap) => {
      timesheetItems.value = snap.docs
        .filter(doc => {
          const subDept = (doc.data().formData?.department || doc.data().department || '').toLowerCase().trim();
          return uniqueMyDepts.includes(subDept);
        })
        .map(d => ({
          id: d.id, type: 'timesheet',
          title: `Auth: ${d.data().formData?.userName || d.data().userName || 'Staff'}`,
          description: `Authorise Week ${d.data().formData?.railWeek} Timesheet`,
          icon: BanknotesIcon, color: '#DB2777',
          to: { path: '/timesheets/review', query: { id: d.id } },
          timestamp: d.data().submittedAt ? new Date(d.data().submittedAt).getTime() : 0
        }));
    }));
  }

  // 4. SUPPORT REQUESTS (ENGINEERING DEPT + PERMISSIONS LOCK)
  const isEngineeringLead = uniqueMyDepts.includes('engineering') && perms?.canViewEngineering;
  if (isEngineeringLead || userStore.role === 'super-admin') {
    unsubs.push(onSnapshot(query(collection(db, 'supportRequestSubmissions'), where('clientId', '==', clientId), where('status', '==', 'Pending')), (snap) => {
      supportItems.value = snap.docs.map(d => ({ 
        id: d.id, type: 'support', 
        title: `Support: ${d.data().humanReadableId || 'REQ'}`, 
        description: `From: ${d.data().formData?.companyDisplayName || 'Client'}`, 
        icon: WrenchScrewdriverIcon, color: '#6366F1', 
        to: { name: 'AdminEngineeringDashboard' }, 
        timestamp: d.data().submittedAt ? new Date(d.data().submittedAt).getTime() : 0 
      }));
    }));
  }

  // 5. FLEET COMPLIANCE (STRICT DEPARTMENTAL SILO)
  if (perms?.canViewVehicles || userStore.isAdmin) {
    unsubs.push(onSnapshot(query(collection(db, 'clients', clientId, 'vehicles')), (snap) => {
      const thirtyDays = new Date(Date.now() + (30 * 24 * 60 * 60 * 1000));
      const items = [];
      snap.forEach(d => {
        const data = d.data();
        const assetDept = (data.department || '').toLowerCase().trim();
        if (uniqueMyDepts.includes(assetDept)) {
          const mot = data.motExpiry ? new Date(data.motExpiry) : null;
          if (mot && mot < thirtyDays) items.push({ id: d.id, type: 'vehicles', title: `Asset: ${data.registration}`, description: 'Compliance Alert', icon: TruckIcon, color: '#EAB308', to: { name: 'AdminVehicleEditor', params: { id: d.id } }, timestamp: Date.now() });
        }
      });
      vehicleItems.value = items;
    }));
  }
};

watch([() => props.targetUserId, () => userStore.isUserProfileLoading], ([, isLoading]) => { if (!isLoading) setupListeners(); }, { immediate: true });
watch(allNotifications, (newVal) => { if (isAuthorisedToView.value) { userStore.setGlobalPendingActionCount(newVal.length); emit('update-count', newVal.length); } }, { deep: true });
onUnmounted(() => unsubs.forEach(u => u()));
</script>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>